import React from 'react';
import { format, parseISO } from 'date-fns';
import { Task } from '../../types/task.types';
import {
  useArchiveTask,
  useRestoreTask,
  useTaskDetail,
  useToggleChecklistItem,
  useUpdateTaskInline,
} from '../../hooks/useTaskQueries';
import { useUserOptions } from '../../hooks/useRegisterQueries';
import { isTaskOverdue, dueStamp, TASK_STATUS_LABEL } from './taskMeta';
import { TaskStatus } from '../../types/task.types';
import { useToast } from '../../hooks/useToast';
import { LedgerSelect, LedgerSelectOption } from '../ui';
import ConfirmDialog from '../common/ConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/cn';

const STATUS_OPTIONS: LedgerSelectOption[] = (Object.keys(TASK_STATUS_LABEL) as TaskStatus[]).map(
  (value) => ({ value, label: TASK_STATUS_LABEL[value] }),
);
const PRIORITY_OPTIONS: LedgerSelectOption[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((p) => ({
  value: p,
  label: p.charAt(0) + p.slice(1).toLowerCase(),
}));

// Same date rules as RegisterPeek: parseISO for date-only fields, local time
// for timestamps.
const stampLocal = (iso: string | null | undefined): string | null =>
  iso ? format(new Date(iso), 'MMM d, yyyy').toUpperCase() : null;
const stampDate = (dateStr: string | null | undefined): string | null =>
  dateStr ? format(parseISO(dateStr), 'MMM d, yyyy').toUpperCase() : null;

interface TaskPeekProps {
  task: Task;
  onClose: () => void;
  onEdit: (task: Task) => void;
  onOpenRisk: (riskId: string) => void;
  // When rendered inside the mobile LedgerSheet, the sheet already supplies the
  // surrounding chrome; drop the split-pane left border and outer padding so
  // they don't double up.
  inSheet?: boolean;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="cap-label">{label}</div>
    <div className="mt-0.5 text-scale-1 text-ink">{children}</div>
  </div>
);

export const TaskPeek: React.FC<TaskPeekProps> = ({ task, onClose, onEdit, onOpenRisk, inSheet }) => {
  const detail = useTaskDetail(task.id);
  const update = useUpdateTaskInline();
  const toggle = useToggleChecklistItem();
  const users = useUserOptions();
  const toast = useToast();
  const archive = useArchiveTask();
  const restore = useRestoreTask();
  const isArchived = (detail.data?.status ?? task.status) === 'ARCHIVED';
  const [confirmingArchive, setConfirmingArchive] = React.useState(false);
  // Archive needs ADMIN or MANAGER on the backend. Rendering it for everyone
  // would hand most users a button that 403s.
  const { isAdmin, isManager, isSuperuser } = usePermissions();
  const canArchive = isAdmin || isManager || isSuperuser;

  const onSaveError = () => toast.error('Save failed');
  const assigneeOptions: LedgerSelectOption[] = [
    { value: '', label: 'Unassigned' },
    ...(users.data ?? []).map((user) => ({ value: user.id, label: user.fullName })),
  ];
  const checklist = detail.data?.checklist_items ?? [];
  const checked = checklist.filter((item) => item.is_complete).length;
  const updatedStamp = stampLocal(detail.data?.updatedAt);

  return (
    <aside aria-label="Task peek" className={cn('bg-paper-raised', !inSheet && 'border-l border-ink p-5')}>
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-scale-0 text-ink-muted">
          {task.reference_number} · {TASK_STATUS_LABEL[task.status]}
        </span>
        <span className="flex gap-3">
          <button type="button" onClick={() => onEdit(task)} className="cap-label cursor-pointer !text-accent hover:underline">
            Edit ↗
          </button>
          {canArchive && !isArchived && (
            <button
              type="button"
              onClick={() => setConfirmingArchive(true)}
              disabled={archive.isPending}
              className="cap-label cursor-pointer text-ink-muted hover:!text-ink disabled:cursor-default disabled:opacity-50"
            >
              Archive
            </button>
          )}
          {/* Risks got this when review pointed out the confirmation promises
              a restore. Tasks were left with the service method and no caller,
              so archiving a task was one way through the product. */}
          {canArchive && isArchived && (
            <button
              type="button"
              onClick={() => restore.mutate(task.id, { onError: onSaveError })}
              disabled={restore.isPending}
              className="cap-label cursor-pointer !text-accent hover:underline disabled:cursor-default disabled:opacity-50"
            >
              {restore.isPending ? 'Restoring…' : 'Restore'}
            </button>
          )}
          <button type="button" aria-label="Close peek" onClick={onClose} className="cursor-pointer text-scale-0 text-ink-muted hover:text-ink">
            ✕
          </button>
        </span>
      </div>

      <h2 className="mt-1.5 font-serif text-scale-4 font-medium text-ink">{task.title}</h2>
      <p className="mb-3 text-scale-1 text-ink-muted">{detail.data?.description || task.description}</p>
      <div className="border-b border-ink" />

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 py-3">
        <Field label="Status">
          <LedgerSelect
            ariaLabel="Status"
            value={task.status}
            options={STATUS_OPTIONS}
            disabled={update.isPending}
            onValueChange={(status) =>
              update.mutate({ id: task.id, data: { status: status as Task['status'] } }, { onError: onSaveError })
            }
          />
        </Field>
        <Field label="Assignee">
          <LedgerSelect
            ariaLabel="Assignee"
            value={task.assignee ?? ''}
            options={assigneeOptions}
            disabled={update.isPending}
            onValueChange={(assignee) =>
              update.mutate({ id: task.id, data: { assignee: assignee === '' ? null : assignee } }, { onError: onSaveError })
            }
          />
        </Field>
        <Field label="Priority">
          <LedgerSelect
            ariaLabel="Priority"
            value={task.priority}
            options={PRIORITY_OPTIONS}
            disabled={update.isPending}
            onValueChange={(priority) =>
              update.mutate({ id: task.id, data: { priority: priority as Task['priority'] } }, { onError: onSaveError })
            }
          />
        </Field>
        <Field label="Due date">
          <span className={cn('font-mono', isTaskOverdue(task) ? 'text-sev-critical' : '')}>
            {dueStamp(task, 'MMM d, yyyy') ?? '—'}
          </span>
        </Field>
        <Field label="Progress">
          {/* The detail, not the list row: a checklist tick moves progress and
              invalidates task-detail, so the row prop is a tick behind. */}
          <span className="font-mono">{detail.data?.progress ?? task.progress ?? 0} %</span>
        </Field>
        {task.riskReference && task.risk && (
          <Field label="Linked risk">
            <button
              type="button"
              onClick={() => onOpenRisk(task.risk as string)}
              className="cursor-pointer font-mono text-accent hover:underline"
            >
              ↳ {task.riskReference}
            </button>
          </Field>
        )}
      </div>

      <div className="cap-label border-b border-ink pb-0.5">
        Checklist · {checklist.length === 0 ? '0' : `${checked} of ${checklist.length}`}
      </div>
      {checklist.length === 0 ? (
        <p className="py-2 font-serif text-scale-1 italic text-ink-muted">No checklist items.</p>
      ) : (
        checklist.map((item) => (
          <button
            key={item.id}
            type="button"
            disabled={toggle.isPending}
            onClick={() => toggle.mutate(item.id, { onError: onSaveError })}
            className="flex w-full cursor-pointer items-baseline gap-2 border-b border-rule-faint py-1.5 text-left text-scale-1 disabled:cursor-default"
          >
            <span aria-hidden className={cn('h-2.5 w-2.5 shrink-0 self-center border border-ink', item.is_complete && 'bg-ink')} />
            <span className={cn('min-w-0 flex-1 truncate', item.is_complete && 'text-ink-muted line-through')}>
              {item.title}
            </span>
          </button>
        ))
      )}

      <div className="cap-label mt-4 border-b border-ink pb-0.5">History</div>
      <div className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">
        <div>Recorded {stampLocal(detail.data?.createdAt ?? task.createdAt)}</div>
        {updatedStamp && <div>Updated {updatedStamp}</div>}
        {task.completedDate && <div>Completed {stampDate(task.completedDate)}</div>}
      </div>
      <ConfirmDialog
        open={confirmingArchive}
        title="Archive this task?"
        message={
          `${task.reference_number} leaves the task list. The record is kept and ` +
          'an administrator can restore it, so nothing is lost.'
        }
        confirmText="Archive"
        severity="warning"
        onCancel={() => setConfirmingArchive(false)}
        onConfirm={() => {
          setConfirmingArchive(false);
          archive.mutate(task.id, {
            onSuccess: () => {
              toast.success(`${task.reference_number} archived`);
              onClose();
            },
            onError: () => toast.error('Archive failed'),
          });
        }}
      />
    </aside>
  );
};
